/**
 * Produits offerts automatiquement (règles : blocks/baubeau-cart-gift.liquid, une par bloc).
 * À chaque évolution du panier, pour chaque règle : si le produit déclencheur y est, le produit offert est
 * ajouté (1 unité, propriété cachée portant l'id de la règle) ; sinon il est retiré. La ligne offerte reçoit
 * une inscription et ses contrôles de quantité sont masqués. Le prix à 0 € dépend de la promotion
 * automatique créée dans l'admin.
 */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const allRules = [...document.querySelectorAll('script[data-baubeau-cart-gift]')].map((el) => JSON.parse(el.textContent));
const rules = allRules.filter((rule) => rule.enabled);
const PROPERTY = '_baubeau_gift';
const SOURCE = 'baubeau-cart-gift';
const jsonHeaders = { 'Content-Type': 'application/json', Accept: 'application/json' };

/** Clé de ligne du panier → inscription de la règle correspondante. */
let giftLines = new Map();
let busy = false;

async function fetchCart() {
  const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
  return response.json();
}

const post = (url, body) => fetch(url, { method: 'POST', headers: jsonHeaders, body: JSON.stringify(body) });

function readGiftLines(cart) {
  giftLines = new Map();
  for (const item of cart.items) {
    const rule = rules.find((candidate) => candidate.id === item.properties?.[PROPERTY]);
    if (rule) giftLines.set(item.key, rule.label);
  }
}

function markRows() {
  document.querySelectorAll('.cart-items__table-row[data-key]').forEach((row) => {
    const label = giftLines.get(row.dataset.key);
    const note = row.querySelector('.baubeau-gift-note');
    row.classList.toggle('baubeau-gift-line', Boolean(label));
    if (label && !note) {
      const anchor = row.querySelector('.cart-items__title-row, .cart-items__product-info');
      const element = document.createElement('p');
      element.className = 'baubeau-gift-note';
      element.textContent = label;
      if (anchor) anchor.after(element);
      else row.querySelector('.cart-items__details')?.append(element);
    } else if (!label && note) {
      note.remove();
    } else if (label && note && note.textContent !== label) {
      note.textContent = label;
    }
  });
}

/** Applique chaque règle ; renvoie true si le panier a été modifié. */
async function applyRules(cart) {
  let changed = false;
  // Cadeau dont la règle est désactivée ou supprimée : retiré du panier.
  for (const item of cart.items) {
    const ruleId = item.properties?.[PROPERTY];
    if (ruleId && !rules.some((rule) => rule.id === ruleId)) {
      if ((await post('/cart/change.js', { id: item.key, quantity: 0 })).ok) changed = true;
    }
  }
  for (const rule of rules) {
    const hasTrigger = cart.items.some((item) => item.product_id === rule.triggerProductId);
    const gift = cart.items.find((item) => item.properties?.[PROPERTY] === rule.id);
    let response = null;

    if (hasTrigger && !gift) {
      response = await post('/cart/add.js', {
        items: [{ id: rule.giftVariantId, quantity: 1, properties: { [PROPERTY]: rule.id } }],
      });
    } else if (hasTrigger && gift.quantity !== 1) {
      response = await post('/cart/change.js', { id: gift.key, quantity: 1 });
    } else if (!hasTrigger && gift) {
      response = await post('/cart/change.js', { id: gift.key, quantity: 0 });
    }
    if (response?.ok) changed = true;
  }
  return changed;
}

async function sync() {
  if (busy) return;
  busy = true;
  try {
    let cart = await fetchCart();
    if (await applyRules(cart)) {
      cart = await fetchCart();
      readGiftLines(cart);
      const deferred = CartLinesUpdateEvent.createPromise();
      document.dispatchEvent(
        new CartLinesUpdateEvent({ action: 'add', context: 'cart', lines: [], promise: deferred.promise })
      );
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { source: SOURCE, items: cart.items, itemCount: cart.item_count },
      });
    } else {
      readGiftLines(cart);
    }
    markRows();
  } catch (error) {
    console.warn('[cart-gift]', error);
  } finally {
    busy = false;
  }
}

if (allRules.length && !window.__baubeauCartGift) {
  window.__baubeauCartGift = true;

  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.source !== SOURCE) sync();
      })
      .catch(() => {});
  });

  let timer = null;
  const observer = new MutationObserver(() => {
    clearTimeout(timer);
    timer = setTimeout(markRows, 60);
  });
  const watch = () =>
    document.querySelectorAll('cart-items-component').forEach((el) => observer.observe(el, { childList: true, subtree: true }));
  watch();
  window.addEventListener('pageshow', sync);
  sync().then(watch);
}
